import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { AGENT_URL } from '@/config/env'
import { apiErrorFrom, fetchJsonWithAuth } from '@/lib/api'
import { organizationMappings } from './model'
import type {
  FieldSource,
  FieldValue,
  SigningConfiguration,
  SigningFact,
  SigningForm,
} from './model'

export function draftPreviewRequest(
  form: SigningForm,
  configuration: SigningConfiguration | undefined,
  managesFields: boolean,
  clientId: string | undefined
) {
  return {
    client_id: clientId,
    mappings: configuration
      ? organizationMappings(form, configuration.mappings)
      : {},
    ...(managesFields
      ? {
          fields: Object.fromEntries(
            form.fields.map((field) => [
              field.name,
              {
                required: field.required,
                ownership: field.ownership,
                source: field.ownership === 'template' ? field.source : null,
              },
            ])
          ),
        }
      : {}),
  }
}

export interface DraftResolution {
  values: Record<string, FieldValue>
  issues: { field: string | null; code: string }[]
  ready: boolean
}

export interface SigningCatalogue {
  facts: SigningFact[]
  annotations: {
    fields: Record<
      string,
      {
        required: boolean
        ownership: 'template' | 'organization'
        source?: FieldSource | null
      }
    >
  } | null
}

export function useSigningFactsQuery(formKey: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  return useQuery({
    queryKey: ['signing-facts', organization?.id, formKey],
    enabled: Boolean(organization?.id),
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error('Sign in to search Facts.')
      return fetchJsonWithAuth<SigningCatalogue>({
        token,
        url: `${AGENT_URL}/signing/forms/${encodeURIComponent(formKey)}/facts`,
        signal,
        errorMessage: 'Could not load Facts.',
      })
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
}

export function useSigningPreviewQuery(
  form: SigningForm,
  configuration: SigningConfiguration | undefined,
  managesFields: boolean,
  clientId: string | undefined
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const request = draftPreviewRequest(
    form,
    configuration,
    managesFields,
    clientId
  )
  return useQuery({
    queryKey: ['signing-preview', organization?.id, form.key, request],
    enabled: Boolean(organization?.id && clientId),
    queryFn: async ({ signal }): Promise<DraftResolution> => {
      const token = await getToken()
      if (!token) throw new Error('Sign in to preview this member.')
      const response = await fetch(
        `${AGENT_URL}/signing/forms/${encodeURIComponent(form.key)}/preview`,
        {
          method: 'POST',
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
          signal,
        }
      )
      if (!response.ok)
        throw await apiErrorFrom(response, 'Could not load preview.')
      return response.json()
    },
    // Member values are transient and never persisted with the configuration.
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
